import React from 'react';
import { useDeviceType, useWindowSize } from '../../utils/hooks.js';
import { BREAKPOINTS } from '../../constants';

const AppPreview = ({ 
  src = '/desktop-image-stack.png',
  alt = 'FootyFlowStates App Preview',
  className = '',
  style = {},
  ...props 
}) => {
  const deviceType = useDeviceType();
  const windowSize = useWindowSize();
  const isMobile = deviceType === 'mobile';
  const isTablet = deviceType === 'tablet';
  const isDesktop = deviceType === 'desktop';

  const getImageStyles = () => {
    if (isMobile) {
      return {
        width: '18rem', // w-72 mobile-md:w-80
        maxWidth: '20rem', // max-w-sm
        height: 'auto',
      };
    }

    if (isTablet) {
      const width = windowSize?.width || BREAKPOINTS.TABLET_MD;
      
      // Surface Duo + iPad Mini range (540-833px) - Prominent but balanced for smaller tablet
      if (width < BREAKPOINTS.TABLET_MD) {
        return {
          width: '34rem', // 544px - Increased from 448px for more prominence
          maxWidth: '90%', // Increased from 85% to allow more space
          height: 'auto',
        };
      }
      // iPad Air range (834-1023px) - Standard tablet size, should be main focal point
      else if (width < BREAKPOINTS.TABLET_LG) {
        return {
          width: '40rem', // 640px - Increased from 512px for main focal point
          maxWidth: '95%', // Increased from 90% to maximize space
          height: 'auto',
        };
      }
      // iPad Pro range (1024-1199px) - Large tablet, app preview should be substantial
      else {
        return {
          width: '44rem', // 704px - Increased from 576px for substantial presence
          maxWidth: '85%', // Take up significant space as main visual element
          height: 'auto',
        };
      }
    }

    // Desktop (1000px+) - 60% of screen width
    if (isDesktop) {
      return {
        width: '60vw',
        height: 'auto',
      };
    }

    // Fallback
    return {
      maxWidth: '100%',
      height: 'auto',
    };
  };

  const imageStyles = getImageStyles();

  // Add responsive classes
  const getResponsiveClasses = () => {
    if (isMobile) {
      return 'w-72 mobile-md:w-80 max-w-sm h-auto';
    }
    if (isTablet) {
      const width = windowSize?.width || BREAKPOINTS.TABLET_MD;
      
      if (width < BREAKPOINTS.TABLET_MD) {
        return 'max-w-[85%] h-auto'; // iPad Mini - Prominent but balanced
      } else if (width < BREAKPOINTS.TABLET_LG) {
        return 'max-w-[90%] h-auto'; // iPad Air - Main visual focus
      } else {
        return 'max-w-[75%] h-auto'; // iPad Pro - Substantial presence
      }
    }
    if (isDesktop) {
      return 'h-auto';
    }
    return 'max-w-full h-auto';
  };

  const responsiveClasses = getResponsiveClasses();

  return (
    <img
      src={src}
      alt={alt}
      style={{ ...imageStyles, ...style }}
      className={`${responsiveClasses} ${className}`}
      {...props}
    />
  );
};

export default AppPreview;